import axios, { AxiosError } from "axios";
import { ApiClientError, mapV2 } from "./v2";
import type {
  BuyerAddress,
  Cart,
  Category,
  CreatedOrder,
  Product,
  Store,
} from "./types";

const marketBase = import.meta.env.VITE_API_MARKET || "/api/market";
const buyerBase = import.meta.env.VITE_API_BUYER || "/api/buyer";

export const marketApi = axios.create({
  baseURL: marketBase,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

export const buyerApi = axios.create({
  baseURL: buyerBase,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

function translateError(error: unknown): never {
  if (error instanceof ApiClientError) throw error;
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 500;
    try {
      mapV2(error.response?.data, status);
    } catch (mapped) {
      throw mapped;
    }
    throw new ApiClientError({
      status,
      code: "API_MARKET__INTERNAL_ERROR",
      title: "Permintaan gagal",
      description: error.message,
    });
  }
  throw error;
}

function attachMapper(
  client: ReturnType<typeof axios.create>,
): ReturnType<typeof axios.create> {
  client.interceptors.response.use(
    (response) => {
      if (
        response.data &&
        typeof response.data === "object" &&
        "metadata" in response.data
      ) {
        response.data = mapV2(response.data, response.status);
      }
      return response;
    },
    (error) => translateError(error),
  );
  return client;
}

attachMapper(marketApi);
attachMapper(buyerApi);

export async function fetchProducts(): Promise<Product[]> {
  const { data } = await marketApi.get<Product[]>("/products");
  return data;
}

export async function fetchProduct(slug: string): Promise<Product> {
  const { data } = await marketApi.get<Product>(
    `/products/${encodeURIComponent(slug)}`,
  );
  return data;
}

export async function fetchCategory(slug: string): Promise<Category> {
  const { data } = await marketApi.get<Category>(
    `/kategori/${encodeURIComponent(slug)}`,
  );
  return data;
}

export async function fetchStore(slug: string): Promise<Store> {
  const { data } = await marketApi.get<Store>(
    `/toko/${encodeURIComponent(slug)}`,
  );
  return data;
}

export async function searchCatalog(q: string): Promise<Product[]> {
  const { data } = await marketApi.get<Product[]>("/search", { params: { q } });
  return data;
}

export async function fetchCart(): Promise<Cart> {
  const { data } = await marketApi.get<Cart>("/cart");
  return data;
}

export async function addCartLine(skuId: string, qty: number): Promise<Cart> {
  const { data } = await marketApi.post<Cart>("/cart/lines", {
    sku_id: skuId,
    qty,
  });
  return data;
}

export async function updateCartLine(skuId: string, qty: number): Promise<Cart> {
  const { data } = await marketApi.patch<Cart>(
    `/cart/lines/${encodeURIComponent(skuId)}`,
    { qty },
  );
  return data;
}

export async function removeCartLine(skuId: string): Promise<Cart> {
  const { data } = await marketApi.delete<Cart>(
    `/cart/lines/${encodeURIComponent(skuId)}`,
  );
  return data;
}

export async function fetchAddresses(): Promise<BuyerAddress[]> {
  const { data } = await buyerApi.get<BuyerAddress[]>("/addresses");
  return data;
}

export async function createOrder(input: {
  addressId: string;
  shippingZoneId?: string;
}): Promise<CreatedOrder> {
  const { data } = await marketApi.post<CreatedOrder>(
    "/orders",
    {
      address_id: input.addressId,
      shipping_zone_id: input.shippingZoneId,
    },
    {
      headers: {
        "Idempotency-Key":
          globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`,
      },
    },
  );
  return data;
}

export function loginUrl(returnPath: string): string {
  return `/akun/login?next=${encodeURIComponent(returnPath)}`;
}

export function registerUrl(returnPath: string): string {
  return `/akun/daftar?next=${encodeURIComponent(returnPath)}`;
}
